import { Platform } from 'react-native';

const EMBED_MESSAGE_SOURCE = 'bookapt-embed';

export const WIDGET_BOOKING_API = 'https://bookapt-payment-api.onrender.com/api';

export type WidgetEmbedMessageType = 'close' | 'booking-complete';

export type WidgetBookingUrlParams = {
  businessId: string;
  serviceId?: string;
};

function readUrlSearchParams(): URLSearchParams | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    const win = globalThis as typeof globalThis & { location?: { search?: string } };
    const search = win.location?.search || '';
    if (!search) {
      return null;
    }
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    return null;
  }
}

export function getWidgetBookingParamsFromUrl(): WidgetBookingUrlParams | null {
  const params = readUrlSearchParams();
  if (!params) {
    return null;
  }
  const widgetBook = params.get('widgetBook');
  const widgetOk = widgetBook === '1' || widgetBook === 'true';
  const businessId =
    params.get('businessId') || params.get('businessid') || '';
  if (!widgetOk || !businessId) {
    return null;
  }
  const serviceId =
    params.get('serviceId') || params.get('serviceid') || undefined;
  return serviceId ? { businessId, serviceId } : { businessId };
}

/**
 * Reads and clears the one-time sign-in handoff token from the URL (see
 * bookingsHandoffService.ts's openBookAptBookingsInNewTab — a widget booking
 * confirmation opens a NEW tab with this token so the customer lands signed
 * in instead of at a login wall). Cleared from the URL bar immediately after
 * reading so a short-lived credential doesn't linger in browser history.
 */
export function consumeBookingsHandoffToken(): string | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  const params = readUrlSearchParams();
  const token = params?.get('authToken') || null;
  if (!token) {
    return null;
  }
  try {
    const win = globalThis as typeof globalThis & {
      location?: { href?: string; pathname?: string; search?: string };
      history?: { replaceState?: (state: unknown, title: string, url?: string) => void };
    };
    const currentParams = new URLSearchParams(win.location?.search || '');
    currentParams.delete('authToken');
    const query = currentParams.toString();
    const cleanUrl = `${win.location?.pathname || ''}${query ? `?${query}` : ''}`;
    win.history?.replaceState?.({}, '', cleanUrl);
  } catch {
    // ignore — worst case the token stays visible in the URL bar
  }
  return token;
}

const WEBAPP_INITIAL_TAB_KEY = 'bookapt_initial_tab';

export function isInIframe(): boolean {
  if (Platform.OS !== 'web') {
    return false;
  }
  try {
    const win = globalThis as typeof globalThis & {
      self?: typeof globalThis;
      top?: typeof globalThis;
    };
    return !!win.self && !!win.top && win.self !== win.top;
  } catch {
    // Cross-origin iframe — assume embedded.
    return true;
  }
}

/** True when booking UI should run as a merchant-site embed (iframe overlay). */
export function isWidgetEmbedContext(): boolean {
  return isWidgetEmbedMode() || isInIframe();
}

/** True for a real "Request Payment" SMS link — app.html?payLink=1&appointmentId=... */
export function isPayLinkEntry(): boolean {
  const params = readUrlSearchParams();
  if (!params) return false;
  return params.get('payLink') === '1' && !!params.get('appointmentId');
}

/**
 * True when a business followed an invitation link (SMS/email — see
 * businessInvitationService, appointmentRequestService, customerInvitationService,
 * all of which link to bookapt.io/join?ref=...). Render rewrites /join to
 * /app.html transparently (the browser keeps showing /join), so pathname
 * still reflects the original request — no query-param name to collide with.
 */
/**
 * Sales rep referral attribution — reps share bookapt.io/join?repCode=XXXXXX
 * on social media or hand it out in person. Unlike consumeBookingsHandoffToken,
 * this is read (not consumed/cleared) at business-account-creation time,
 * which can be several screens after the initial /join landing — since this
 * is a client-side SPA (App.tsx's currentScreen state, not real routing),
 * window.location.search stays constant across those screen transitions
 * within the same session, so a plain repeatable read is enough; no need to
 * persist it into storage. Must be captured at account-creation time or
 * it's unrecoverable afterward — see unifiedAuthService.ts.
 */
export function getReferralRepCodeFromUrl(): string | null {
  const params = readUrlSearchParams();
  const code = params?.get('repCode');
  return code ? code.trim().toUpperCase() : null;
}

export function isJoinBusinessEntry(): boolean {
  if (Platform.OS !== 'web') return false;
  try {
    const win = globalThis as typeof globalThis & { location?: { pathname?: string } };
    return win.location?.pathname === '/join';
  } catch {
    return false;
  }
}

export function isWidgetEmbedBookingEntry(): boolean {
  // businessId + widgetBook=1 is itself the signal to go straight to booking —
  // true both for a merchant-site iframe embed AND a direct top-level link
  // (front-desk QR code, Google Business Profile, or an emailed /b/:businessId
  // link). Requiring isWidgetEmbedContext() here used to make every one of
  // those direct-link cases fall through to the generic guest-welcome screen
  // instead of the business-specific booking flow the link promised.
  return !!getWidgetBookingParamsFromUrl();
}

export function isWidgetEmbedMode(): boolean {
  if (Platform.OS !== 'web') {
    return false;
  }
  try {
    const win = globalThis as typeof globalThis & { location?: { search?: string } };
    const search = win.location?.search || '';
    if (!search) {
      return false;
    }
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const embed = params.get('embed');
    return embed === '1' || embed === 'true';
  } catch {
    return false;
  }
}

export function notifyWidgetEmbedParent(
  type: WidgetEmbedMessageType,
  payload?: Record<string, unknown>,
): void {
  if (Platform.OS !== 'web' || !isWidgetEmbedContext()) {
    return;
  }
  try {
    const win = globalThis as typeof globalThis & {
      parent?: { postMessage?: (data: unknown, targetOrigin: string) => void };
    };
    if (!win.parent || win.parent === win) {
      return;
    }
    win.parent.postMessage(
      {
        source: EMBED_MESSAGE_SOURCE,
        type,
        ...(payload || {}),
      },
      '*',
    );
  } catch {
    // ignore cross-origin errors
  }
}

/** After widget booking in a standalone tab (not merchant iframe), open the main web app on Appointments. */
export function navigateToWebAppAfterWidgetBooking(): void {
  if (Platform.OS !== 'web') {
    return;
  }
  try {
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    storage?.setItem(WEBAPP_INITIAL_TAB_KEY, 'appointments');
    const win = globalThis as typeof globalThis & {
      location?: { origin?: string; pathname?: string; href?: string };
    };
    const origin = win.location?.origin;
    if (!origin) {
      return;
    }
    const pathname = win.location?.pathname || '/app.html';
    const appPath = pathname.includes('app.html')
      ? pathname.replace(/app\.html.*$/, 'app.html')
      : `${pathname.replace(/\/?$/, '')}/app.html`;
    win.location!.href = `${origin}${appPath}?tab=appointments`;
  } catch {
    // ignore
  }
}

export function consumeWebAppInitialTab(): 'appointments' | 'dashboard' | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    const win = globalThis as typeof globalThis & { location?: { search?: string } };
    const search = win.location?.search || '';
    if (search) {
      const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
      const tabParam = params.get('tab') || params.get('initialTab');
      if (tabParam === 'appointments' || tabParam === 'dashboard') {
        return tabParam;
      }
    }
  } catch {
    // ignore
  }
  try {
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    const tab = storage?.getItem(WEBAPP_INITIAL_TAB_KEY);
    if (tab === 'appointments' || tab === 'dashboard') {
      storage?.removeItem(WEBAPP_INITIAL_TAB_KEY);
      return tab;
    }
  } catch {
    // ignore
  }
  return null;
}

/** Skip duplicate success alerts when the merchant-site iframe overlay is about to close. */
export function shouldSuppressWidgetEmbedSuccessAlert(): boolean {
  return isInIframe() && isWidgetEmbedContext();
}

/** Payload sent to the merchant page when a widget embed booking finishes. */
export type WidgetProductPick = {
  quantity: number;
  selectedOptions: Record<string, string>;
};

const WIDGET_PRODUCT_PICK_PREFIX = 'bookapt_widget_pick_';

export function widgetProductPickStorageKey(
  businessId: string,
  serviceId: string,
): string {
  return `${WIDGET_PRODUCT_PICK_PREFIX}${businessId}_${serviceId}`;
}

/** Read and clear product options chosen in the merchant-site widget before embed opens. */
export function consumeWidgetProductPick(
  businessId: string,
  serviceId: string,
): WidgetProductPick | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    if (!storage) {
      return null;
    }
    const key = widgetProductPickStorageKey(businessId, serviceId);
    const raw = storage.getItem(key);
    storage.removeItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as WidgetProductPick;
    if (
      !parsed ||
      typeof parsed.quantity !== 'number' ||
      parsed.quantity < 1 ||
      !parsed.selectedOptions ||
      typeof parsed.selectedOptions !== 'object'
    ) {
      return null;
    }
    return {
      quantity: Math.floor(parsed.quantity),
      selectedOptions: parsed.selectedOptions,
    };
  } catch {
    return null;
  }
}

export function buildWidgetEmbedBookingCompletePayload(booking: {
  id: string;
  businessId: string;
  serviceName?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  customerData?: { email?: string; name?: string };
  businessData?: { name?: string };
}): Record<string, unknown> {
  const emailSentAt = new Date().toLocaleString();
  return {
    appointmentId: booking.id,
    businessId: booking.businessId,
    serviceName: booking.serviceName,
    appointmentDate: booking.appointmentDate,
    appointmentTime: booking.appointmentTime,
    customerEmail: booking.customerData?.email || '',
    customerName: booking.customerData?.name || '',
    businessName: booking.businessData?.name || '',
    emailSentAt,
  };
}
